import { useState, useCallback, useRef, useEffect } from 'react';
import * as api from '../api/client';

/**
 * Build a default tool config from a live tools array fetched from the backend.
 * All tools default to enabled=false, forced=false.
 */
export function buildDefaultToolConfig(tools) {
  return Object.fromEntries(
    tools.map(t => [t.name, { enabled: false, forced: false }])
  );
}

/**
 * Merge server-side session data with the live tools list so that:
 *   - tool_config from the DB is used when present
 *   - any newly added tool that wasn't in the saved config defaults to disabled
 *   - save_to_kb_default comes from the DB (not localStorage)
 */
function mergeSessionWithTools(session, tools) {
  const defaultConfig = buildDefaultToolConfig(tools);
  return {
    ...session,
    saveToKbByDefault: session.save_to_kb_default ?? false,
    // Merge: DB config wins for known tools; new tools get their default
    toolConfig: { ...defaultConfig, ...(session.tool_config ?? {}) },
  };
}

export function useSessions(tools) {
  const [sessions, setSessions] = useState([]);

  // Keep a ref to the latest tools value so refresh() can always access it
  // without needing tools in its dependency array — this prevents the
  // identity of refresh from changing every time tools is fetched, which
  // would cause infinite useEffect re-fires in App.js.
  const toolsRef = useRef(tools);
  useEffect(() => { toolsRef.current = tools; }, [tools]);

  const refresh = useCallback(async () => {
    const data = await api.getSessions();
    setSessions(data.map(s => mergeSessionWithTools(s, toolsRef.current)));
  }, []); // stable — reads tools via ref, not closure

  const remove = useCallback(async (id) => {
    await api.deleteSession(id);
    setSessions(prev => prev.filter(s => s.id !== id));
  }, []);

  const removeMany = useCallback(async (ids) => {
    await api.bulkDeleteSessions(ids);
    setSessions(prev => prev.filter(s => !ids.includes(s.id)));
  }, []);

  const update = useCallback(async (id, { name, saveToKbByDefault, systemPrompt, toolConfig }) => {
    // Three independent PATCH calls — each owns its own concern.
    await api.renameSession(id, name);
    await api.updateSessionSystemPrompt(id, systemPrompt ?? null);
    await api.updateSessionSettings(id, {
      saveToKbDefault: saveToKbByDefault,
      toolConfig,
    });

    setSessions(prev =>
      prev.map(s => s.id === id
        ? {
            ...s,
            name,
            saveToKbByDefault,
            system_prompt: systemPrompt ?? null,
            toolConfig,
            // Keep DB-shape fields in sync so a subsequent refresh() is consistent
            save_to_kb_default: saveToKbByDefault,
            tool_config: toolConfig,
          }
        : s
      )
    );
  }, []);

  const getSessionSettings = useCallback((id) => {
    const defaultConfig = buildDefaultToolConfig(tools);
    const session = sessions.find(s => s.id === id);
    return {
      saveToKbByDefault: session?.saveToKbByDefault ?? false,
      toolConfig: session?.toolConfig ?? defaultConfig,
    };
  }, [sessions, tools]);

  return { sessions, setSessions, refresh, remove, removeMany, update, getSessionSettings };
}